import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { IOption } from 'src/app/components/shared/form/components/form-select/form-select.component';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HandoverReferenceNums } from 'src/app/core/models/reports/exceptions-reports.model';
import { ExceptionsReportsService } from 'src/app/core/services/reports/exceptions/exceptions-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import {
  CONSTS,
  HandoverScanReportFormGroup,
  REPORT_FILE_NAME,
} from './handover-scan-report.constants';

@Component({
  selector: 'app-handover-scan-report',
  templateUrl: './handover-scan-report.component.html',
  styleUrls: ['./handover-scan-report.component.scss'],
  standalone: false,
})
export class HandoverScanReportComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: HandoverScanReportFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private fb: FormBuilder = inject(FormBuilder);
  private dialogRef: MatDialogRef<HandoverScanReportComponent> = inject(
    MatDialogRef<HandoverScanReportComponent>,
  );
  private exceptionReports: ExceptionsReportsService = inject(
    ExceptionsReportsService,
  );
  private commonUtils: CommonUtils = inject(CommonUtils);
  private reportUtils: ReportsUtils = inject(ReportsUtils);

  readonly CONSTS = CONSTS;
  readonly IMAGES = IMAGE_SRC;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  handoverScanReportForm!: FormGroup;
  location!: string;
  refNumOptions: IOption[] = [];

  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        mergeMap((user) => {
          this.currDate = new Date();
          this.minDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() - 30,
          );
          this.maxDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() + 14,
          );

          this.handoverScanReportForm = this.fb.group({
            date: [this.currDate],
            refNums: [],
          });

          this.location = user?.location;
          return this.getReferenceNumbers();
        }),
      )
      .subscribe();
  }

  onExit() {
    this.commonUtils.openExitModal(this.handoverScanReportForm.dirty);
  }

  onAccept() {
    this.exceptionReports
      .getHandoverReports(
        this.location,
        this.commonUtils.getFormattedDate(
          this.handoverScanReportForm.get('date')?.value,
        ),
        this.handoverScanReportForm.get('refNums')?.value,
      )
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((data) => {
        const exceptionReports = data.response.handoverScan;

        if (exceptionReports?.length) {
          const customHeader = [['', '', 'Handover Scan Report', '', '']];
          this.reportUtils.exportCSVReport(
            exceptionReports,
            REPORT_FILE_NAME,
            customHeader,
          );
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }
        this.dialogRef.close();
      });
  }

  getReferenceNumbers() {
    return this.exceptionReports
      .getHandoverReferenceNumbers(
        this.location,
        this.commonUtils.getFormattedDate(
          this.handoverScanReportForm.get('date')?.value,
        ),
      )
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((refNumbers) => {
          const formattedValues =
            refNumbers.response.handoverScanRefNumbers.map(
              (value: HandoverReferenceNums) => {
                return {
                  label: value.referenceNumber,
                  value: value.referenceNumber,
                };
              },
            );
          this.refNumOptions = [
            {
              label: $localize`All`,
              value: 'All',
            },
            ...formattedValues,
          ];
          this.handoverScanReportForm.get('refNums')?.setValue('All');
        }),
      );
  }

  onDateChanges() {
    this.getReferenceNumbers().subscribe();
  }
}
